import { PureComponent, ReactNode } from 'react';

import { FormattedMessage } from 'react-intl';

import CustomThemeButton from '@atlaskit/button/custom-theme-button';
import { Box, xcss } from '@atlaskit/primitives';

import { CopyableErrorCode } from './copyable-error-code';
import messages from './error-state.i18n';
import GenericMessage from './generic-message';

type ErrorStateProps = {
  header?: ReactNode;
  description?: ReactNode;
  onClick?: () => void;
  // NOTE: Passing the SentryID is for an for an in-flight project.
  // Please do not use until ATLAS-89472 has shipped.
  sentryId?: string;
  onCopyErrorCodeClicked?: () => void;
};

const sentryIdContainerStyles = xcss({
  margin: 'space.100',
});
// generic contact support error text should be included
// anytime a user is being shown an error state or flag that
// includes a sentryId
export function GenericContactSupportErrorText({
  hasCopyableCode = false,
  tagName = 'p',
}: {
  hasCopyableCode: boolean;
  tagName?: keyof JSX.IntrinsicElements;
}) {
  const supportUrl = 'https://support.atlassian.com/contact/';

  const genericMessage = hasCopyableCode
    ? messages.errorDescriptionWithCode
    : messages.errorDescription;

  return (
    <FormattedMessage
      tagName={tagName}
      {...genericMessage}
      values={{
        contactSupport: (
          <a href={supportUrl} target="_blank" rel="noopener noreferrer">
            <FormattedMessage {...messages.contactSupport} />
          </a>
        ),
      }}
    />
  );
}

class ErrorState extends PureComponent<ErrorStateProps> {
  render() {
    const { header, description, onClick, sentryId, onCopyErrorCodeClicked } =
      this.props;

    return (
      <GenericMessage
        iconType="error"
        title={header || <FormattedMessage {...messages.errorHeader} />}
      >
        {description || (
          <GenericContactSupportErrorText hasCopyableCode={!!sentryId} />
        )}
        {/* render copyable sentryId if the Sentry ID is passed */}
        {sentryId && (
          <Box xcss={sentryIdContainerStyles} as="div">
            <CopyableErrorCode
              source={sentryId}
              onCopyButtonClicked={onCopyErrorCodeClicked}
            />
          </Box>
        )}
        {onClick && (
          <CustomThemeButton appearance="link" onClick={this.props.onClick}>
            <FormattedMessage {...messages.errorLink} />
          </CustomThemeButton>
        )}
      </GenericMessage>
    );
  }
}

export default ErrorState;
